import { useEffect } from 'react';
import { useLanguage } from '../lib/language';
export default function SEO({ title, description, keywords = [], image, url, type = 'website', product, }) {
    const { language } = useLanguage();
    const defaultTitle = language === 'es'
        ? 'Machete Supplements - Suplementos Naturales para Salud y Bienestar'
        : 'Machete Supplements - Natural Health & Wellness';
    const defaultDescription = language === 'es'
        ? 'Suplementos naturales premium respaldados por la ciencia para sueño, concentración, energía y bienestar general. Probados en laboratorio, sin aditivos artificiales.'
        : 'Premium natural supplements backed by science for sleep, focus, energy, and overall wellness. Lab-tested, no artificial additives.';
    const defaultKeywords = language === 'es'
        ? [
            'suplementos naturales',
            'suplementos para dormir',
            'suplementos de energía',
            'suplementos de concentración',
            'vitaminas naturales',
            'minerales',
            'productos de salud',
            'bienestar',
            'probado en laboratorio',
            'sin aditivos artificiales',
            'suplementos México',
        ]
        : [
            'natural supplements',
            'sleep supplements',
            'energy supplements',
            'focus supplements',
            'natural vitamins',
            'minerals',
            'health products',
            'wellness',
            'lab tested',
            'no artificial additives',
            'supplements Mexico',
        ];
    const defaultImage = 'https://zeltghlhnsezwrfoktdk.supabase.co/storage/v1/object/public/products/machete-supplements-og.jpg';
    const siteUrl = 'https://machete-supplements.vercel.app';
    const finalTitle = title ? `${title} | Machete Supplements` : defaultTitle;
    const finalDescription = description || defaultDescription;
    const finalKeywords = [...defaultKeywords, ...keywords].join(', ');
    const finalImage = image || defaultImage;
    const finalUrl = url ? `${siteUrl}${url}` : siteUrl;
    useEffect(() => {
        // Update document title
        document.title = finalTitle;
        // Update or create meta tags
        updateMetaTag('name', 'description', finalDescription);
        updateMetaTag('name', 'keywords', finalKeywords);
        updateMetaTag('name', 'author', 'Machete Supplements');
        updateMetaTag('name', 'robots', 'index, follow');
        updateMetaTag('name', 'googlebot', 'index, follow');
        // Language
        document.documentElement.lang = language;
        // Open Graph tags
        updateMetaTag('property', 'og:title', finalTitle);
        updateMetaTag('property', 'og:description', finalDescription);
        updateMetaTag('property', 'og:type', type);
        updateMetaTag('property', 'og:url', finalUrl);
        updateMetaTag('property', 'og:image', finalImage);
        updateMetaTag('property', 'og:image:width', '1200');
        updateMetaTag('property', 'og:image:height', '630');
        updateMetaTag('property', 'og:site_name', 'Machete Supplements');
        updateMetaTag('property', 'og:locale', language === 'es' ? 'es_MX' : 'en_US');
        // Twitter Card tags
        updateMetaTag('name', 'twitter:card', 'summary_large_image');
        updateMetaTag('name', 'twitter:title', finalTitle);
        updateMetaTag('name', 'twitter:description', finalDescription);
        updateMetaTag('name', 'twitter:image', finalImage);
        // Canonical URL
        updateLinkTag('canonical', finalUrl);
        // Product structured data
        if (product) {
            const structuredData = {
                '@context': 'https://schema.org',
                '@type': 'Product',
                name: product.name,
                description: product.description,
                image: product.image,
                brand: {
                    '@type': 'Brand',
                    name: product.brand,
                },
                offers: {
                    '@type': 'Offer',
                    price: product.price,
                    priceCurrency: product.currency,
                    availability: product.availability === 'in stock'
                        ? 'https://schema.org/InStock'
                        : 'https://schema.org/OutOfStock',
                    url: finalUrl,
                },
            };
            updateStructuredData('product-schema', structuredData);
        }
    }, [finalTitle, finalDescription, finalKeywords, finalImage, finalUrl, language, type, product]);
    // Organization structured data (always present)
    useEffect(() => {
        const organizationData = {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Machete Supplements',
            url: siteUrl,
            logo: `${siteUrl}/logopeter.png`,
            description: defaultDescription,
            contactPoint: {
                '@type': 'ContactPoint',
                email: 'contact@bluewaves.mx',
                contactType: 'Customer Service',
            },
            sameAs: [
            // Add social media links here when available
            ],
        };
        updateStructuredData('organization-schema', organizationData);
    }, []);
    return null;
}
function updateMetaTag(attr, key, content) {
    let element = document.querySelector(`meta[${attr}="${key}"]`);
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, key);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content);
}
function updateLinkTag(rel, href) {
    let element = document.querySelector(`link[rel="${rel}"]`);
    if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
    }
    element.setAttribute('href', href);
}
function updateStructuredData(id, data) {
    let element = document.getElementById(id);
    if (!element) {
        element = document.createElement('script');
        element.id = id;
        element.type = 'application/ld+json';
        document.head.appendChild(element);
    }
    element.textContent = JSON.stringify(data);
}
